import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle, Eye, EyeOff, Search } from 'lucide-react';

const inputVariants = cva(
  'flex w-full rounded-lg border bg-white px-3 py-2 text-sm transition-all duration-150 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:placeholder:text-gray-500',
  {
    variants: {
      variant: {
        default:
          'border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:focus:border-brand-400',
        error:
          'border-error-500 focus:border-error-500 focus:ring-2 focus:ring-error-500/20',
        success:
          'border-success-500 focus:border-success-500 focus:ring-2 focus:ring-success-500/20',
      },
      inputSize: {
        sm: 'h-8 text-xs',
        md: 'h-10 text-sm',
        lg: 'h-12 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'md',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, inputSize, type, label, helperText, error, success, leftIcon, rightIcon, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isPassword = type === 'password';
    const currentVariant = error ? 'error' : success ? 'success' : variant;

    return (
      <div className="w-full">
        {label && (
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {leftIcon}
            </div>
          )}
          <input
            type={isPassword ? (showPassword ? 'text' : 'password') : type}
            className={cn(
              inputVariants({ variant: currentVariant, inputSize }),
              leftIcon && 'pl-10',
              (rightIcon || isPassword) && 'pr-10',
              className
            )}
            ref={ref}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
          {rightIcon && !isPassword && (
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>
        {(error || success || helperText) && (
          <div className="mt-1.5 flex items-center gap-1">
            {error && (
              <>
                <AlertCircle className="h-3.5 w-3.5 text-error-500" />
                <span className="text-xs text-error-500">{error}</span>
              </>
            )}
            {success && (
              <>
                <CheckCircle className="h-3.5 w-3.5 text-success-500" />
                <span className="text-xs text-success-500">{success}</span>
              </>
            )}
            {!error && !success && helperText && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{helperText}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

// Search Input variant
export interface SearchInputProps extends Omit<InputProps, 'leftIcon' | 'type'> {
  onSearch?: (value: string) => void;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onSearch, onChange, ...props }, ref) => {
    return (
      <Input
        ref={ref}
        type="search"
        leftIcon={<Search className="h-4 w-4" />}
        onChange={(e) => {
          onChange?.(e);
          onSearch?.(e.target.value);
        }}
        {...props}
      />
    );
  }
);
SearchInput.displayName = 'SearchInput';

// Textarea
export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  error?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, helperText, error, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </label>
        )}
        <textarea
          className={cn(
            'flex min-h-[80px] w-full rounded-lg border px-3 py-2 text-sm transition-all duration-150 placeholder:text-gray-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            error
              ? 'border-error-500 focus:ring-2 focus:ring-error-500/20'
              : 'border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700',
            'bg-white dark:bg-gray-800 dark:placeholder:text-gray-500',
            className
          )}
          ref={ref}
          {...props}
        />
        {(error || helperText) && (
          <div className="mt-1.5 flex items-center gap-1">
            {error ? (
              <>
                <AlertCircle className="h-3.5 w-3.5 text-error-500" />
                <span className="text-xs text-error-500">{error}</span>
              </>
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-400">{helperText}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Input, SearchInput, Textarea, inputVariants };
